import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { handleSupabaseError } from '../lib/errors'
import { Run, CreateRun, UpdateRun, RunWithDetails, RunWithSummary } from '../types'

export function useRuns() {
  return useQuery({
    queryKey: ['runs'],
    queryFn: async (): Promise<RunWithSummary[]> => {
      const { data, error } = await supabase
        .from('runs')
        .select(`
          *,
          segments (id, distance_km),
          aid_stations (id),
          plan_items (id)
        `)
        .order('date', { ascending: false })
      
      if (error) throw handleSupabaseError(error)
      return data
    }
  })
}

export function useRun(id: string) {
  return useQuery({
    queryKey: ['runs', id],
    queryFn: async (): Promise<Run> => {
      const { data, error } = await supabase
        .from('runs')
        .select('*')
        .eq('id', id)
        .single()
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    enabled: !!id
  })
}

export function useRunWithDetails(id: string) {
  return useQuery({
    queryKey: ['runs', id, 'details'],
    queryFn: async (): Promise<RunWithDetails> => {
      const { data, error } = await supabase
        .from('runs')
        .select(`
          *,
          segments (*),
          aid_stations (*),
          plan_items (*)
        `)
        .eq('id', id)
        .order('order_index', { foreignTable: 'segments' })
        .single()
      
      if (error) throw handleSupabaseError(error)
      return data as RunWithDetails
    },
    enabled: !!id
  })
}

export function useCreateRun() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (newRun: CreateRun): Promise<Run> => {
      const { data, error } = await supabase
        .from('runs')
        .insert(newRun)
        .select()
        .single()
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['runs'] })
    }
  })
}

export function useUpdateRun() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateRun }): Promise<Run> => {
      const { data, error } = await supabase
        .from('runs')
        .update(updates)
        .eq('id', id)
        .select()
        .single()
      
      if (error) throw handleSupabaseError(error)
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['runs'] })
      queryClient.invalidateQueries({ queryKey: ['runs', data.id] })
    }
  })
}

export function useDeleteRun() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase
        .from('runs')
        .delete()
        .eq('id', id)
      
      if (error) throw handleSupabaseError(error)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['runs'] })
    }
  })
}